import { Capacitor } from "@capacitor/core";
import { ENV } from "../environments/environment";

/**
 * First path segments that are route keywords, NOT study slugs. Used when parsing
 * the slug out of the URL in multi-study mode so a slug-less path (e.g. "/welcome")
 * doesn't get mistaken for a study slug.
 */
const RESERVED_SEGMENTS = new Set(["welcome", "tabs"]);

/** True when this deployment serves exactly one study. Native is always single-study. */
export function isSingleStudy(): boolean {
  if (Capacitor.isNativePlatform()) return true;
  return ENV.STUDY.mode === "single";
}

/**
 * The pinned numeric study id in single-study mode (so we skip the slug -> id lookup);
 * null in multi-study mode, where the id is resolved at runtime via the welcome call.
 */
export function getConfiguredStudyId(): number | null {
  if (!isSingleStudy()) return null;
  if (typeof ENV.STUDY.id !== "number") {
    throw new Error(
      "Single-study deployment is misconfigured: ENV.STUDY.id must be a number"
    );
  }
  return ENV.STUDY.id;
}

/**
 * The pinned is_gamified flag in single-study mode (so we skip the is_gamified call);
 * null in multi-study mode, where it is fetched per study via the API.
 */
export function getConfiguredIsGamified(): boolean | null {
  if (!isSingleStudy()) return null;
  return ENV.STUDY.is_gamified;
}

/** Read the leading path segment as a slug, ignoring reserved route keywords. SSR-safe. */
function parseSlugFromPath(): string | null {
  if (typeof window === "undefined") return null;
  const [, first] = window.location.pathname.split("/");
  if (!first || RESERVED_SEGMENTS.has(first)) return null;
  return first;
}

export function getStudySlug(): string {
  // Single-study (and native): always the configured slug — the URL needs no slug.
  if (isSingleStudy()) {
    if (!ENV.STUDY.slug) {
      throw new Error(
        "Single-study deployment is misconfigured: ENV.STUDY.slug must be set"
      );
    }
    return ENV.STUDY.slug;
  }
  // Multi-study (web): the slug comes from the URL. Empty on a slug-less landing —
  // only the NoSlug page should render in that state.
  return parseSlugFromPath() ?? ENV.STUDY.slug ?? "";
}

/**
 * Native deep-link scheme = the configured study slug. /!\ must be all lowercase,
 * without spaces or special characters, and match the native manifests' custom scheme.
 */
export function getUrlScheme() {
  return getStudySlug().toLowerCase();
}
